import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, Badge, Modal, Button } from 'react-bootstrap';
import { useAuth } from '../security/AuthContext';
import { apiClient } from '../api/ApiClient';
import useFastPhotos from '../fastPhotos/useFastPhotos';
import './RemoveAccountComponent.css';

function RemoveAccountComponent() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [requestCompleted, setRequestCompleted] = useState(false);
    const [errorDetails, setErrorDetails] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    
    const navigate = useNavigate();
    const authContext = useAuth();
    const {fastPhotos, loading: fastPhotosLoading, error: fastPhotosError} = useFastPhotos();

    console.log("Component rendering. showConfirmModal =", showConfirmModal); // Debug log

    function handleEmailChange(event) {
        setEmail(event.target.value);
    }

    function handlePasswordChange(event) {
        setPassword(event.target.value);
    }

    // Handle click on button to show confirmation
    function handleConfirmButtonClick() {
        console.log("Confirm button clicked"); // Debug log
        
        // Validate inputs
        if (!email || !password) {
            setShowErrorMessage(true);
            setErrorDetails('Please enter both email and password to confirm account removal.');
            return;
        }

        // Show confirmation modal
        console.log("Setting showConfirmModal to true"); // Debug log
        setShowConfirmModal(true);
    }
    
    // Close the modal
    function handleCloseModal() {
        console.log("Closing modal"); // Debug log
        setShowConfirmModal(false);
    }
    
    // Handle click on a close match profile
    const handleCloseMatchClick = (e, memberId) => {
        e.preventDefault();
        const token = localStorage.getItem('token');

        if (token) {
            // Record the profile view
            recordProfileView(memberId);
            // Navigate to the profile page
            navigate(`/profile/view/1/${memberId}`);
        } else {
            navigate('/login');
        }
    };

    // Function to record profile view
    const recordProfileView = async (memberId) => {
        try {
            console.log('Recording view for profile ID:', memberId);
            
            const response = await apiClient.post('/rest/recordView', {
                viewedMemberId: memberId
            });
            
            console.log('View recorded response:', response.data);
        } catch (error) {
            console.error('Error recording profile view:', error);
        }
    };
    
    // Function to handle the actual account removal after confirmation
    async function confirmAccountRemoval() {
        console.log("Confirming account removal"); // Debug log
        setShowConfirmModal(false); // Close the modal
        
        try {
            console.log("Sending API request"); // Debug log
            // Call the new endpoint - no need to send email/password in the payload
            // as the JWT token contains the user ID
            const response = await apiClient.post('/rest/profile/removeaccount', {
                confirmRemoval: true
            });
            
            console.log('Account removal response:', response.data);
            
            if (response.status === 200 && 
                (response.data.removalStatus === 'success' || response.data.removalStatus === 'pass')) {
                console.log("API call successful"); // Debug log
                // Clear auth data using the standard logout function
                authContext.logout();
                
                // For extra thoroughness, explicitly clear ALL browser storage
                localStorage.clear(); // Clears ALL localStorage items including tokens, username, _grecaptcha, etc.
                sessionStorage.clear(); // Clear any session storage items too
                
                // Clear any cookies by setting their expiration to the past
                document.cookie.split(";").forEach(function(c) {
                    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
                });
                
                setShowErrorMessage(false);
                setRequestCompleted(true);
                
                // Redirect to login after a short delay
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                console.log("API call returned error status"); // Debug log
                setShowErrorMessage(true);
                setErrorDetails('Account removal failed. Please try again later.');
                setRequestCompleted(false);
            }
        } catch (error) {
            console.error('Account removal failed:', error);
            setShowErrorMessage(true);
            setErrorDetails('An error occurred while processing your request. Please try again later.');
            setRequestCompleted(false);
        }
    }

    return (
        <div className="container-1024">
            <div className="results-area">
                <div className="results-content">
                    <div className="results-center-details">
                        <div className="remove-account-container">
                            <div className="remove-account-box">
                                <h2 className="remove-account-title">Cancel your account by confirming your ID</h2>
                                
                                {!requestCompleted ? (
                                    <>
                                        <div className="form-field">
                                            <input
                                                type="text"
                                                placeholder="Email"
                                                className="form-input"
                                                value={email}
                                                onChange={handleEmailChange}
                                            />
                                        </div>
                                        <div className="form-field">
                                            <input
                                                type="password"
                                                placeholder="Password"
                                                className="form-input"
                                                value={password}
                                                onChange={handlePasswordChange}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleConfirmButtonClick();
                                                }}
                                            />
                                        </div>
                                        <button
                                            className="auth-button remove-button"
                                            onClick={handleConfirmButtonClick}
                                        >
                                            <span className="heart-icon">♥</span> Confirm ID to cancel
                                        </button>
                                    </>
                                ) : null}
                                
                                {showErrorMessage && (
                                    <div className="error-message">
                                        {errorDetails || "Request error"}
                                    </div>
                                )}
                                
                                {requestCompleted && (
                                    <div className="success-message">
                                        Your account has been successfully removed. You will be redirected to the login page shortly.
                                    </div>
                                )}
                                
                                {!requestCompleted ? (
                                    <div className="instructions">
                                        <p>For your security, please enter your email and password to confirm account removal. This action cannot be undone. Access to all your data and account will be permanently restricted/removed.</p>
                                    </div>
                                ) : null}
                                
                                <div className="action-links">
                                    {!requestCompleted ? (
                                        <Link to="/email/inbox" className="back-link">Cancel and return to Inbox</Link>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="results-right-sidebar">
                        <h5>Close Matches</h5>
                        {fastPhotosLoading ? (
                            <p>Loading...</p>
                        ) : fastPhotosError ? (
                            <p>{fastPhotosError}</p>
                        ) : fastPhotos && fastPhotos.length > 0 ? (
                            fastPhotos.map((photo, index) => (
                                <Card key={index} className="fast-photo-card-side">
                                    <a 
                                        href={`/profile/view/1/${photo[0]}`}
                                        onClick={(e) => handleCloseMatchClick(e, photo[0])}
                                    >
                                        <Card.Img 
                                            variant="top" 
                                            src={`https://pics.netfixup.com/${photo[7]}${photo[6]}`}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = "https://pics.netfixup.com/nfu_default_img/addNewUserPhoto.jpg";
                                            }}
                                        />
                                    </a>
                                    <Badge pill bg="light" text="dark">
                                        {photo[2]}
                                    </Badge>
                                </Card>
                            ))
                        ) : (
                            <p>No Close Matches Found</p>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Modal defined outside the container for better React performance */}
            <Modal 
                show={showConfirmModal} 
                onHide={handleCloseModal}
                backdrop="static"
                keyboard={false}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Account Removal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="warning-text">
                        <strong>WARNING: This action is permanent and cannot be undone.</strong>
                    </p>
                    <p>Your account will be deactivated including:</p>
                    <ul>
                        <li>Profile information</li>
                        <li>Messages</li>
                        <li>Photos</li>
                        <li>Match visibility</li>
                    </ul>
                    <p>Access to your account and its features will be terminated.</p>
                    <p>Are you absolutely sure you want to proceed?</p>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={confirmAccountRemoval}>
                        Yes, Delete My Account
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default RemoveAccountComponent;